import {useCallback} from 'react';

import {useApolloClient, useQuery} from '@apollo/react-hooks';

import type {TwoLineOptionWithIcon} from 'shells/select-two-line-icon/select-two-line-icon';

import type {MeetingSortOrder} from 'nutshell-graphql-types';
import type {
    GetMeetingsForReport as GetMeetingsForReportQuery,
    GetMeetingsForReportVariables,
    GetMeetingsForReport_meetings as MeetingForReport,
    GetMeetingForReportValue as GetMeetingForReportValueQuery,
    GetMeetingForReportValueVariables,
} from 'nutshell-graphql-types';

import {getMeetingOption} from '../../helpers';

import GET_MEETING_FOR_REPORT_VALUE from '../queries/get-meeting-for-report-value.graphql';
import GET_MEETINGS_FOR_REPORT from '../queries/get-meetings-for-report.graphql';

const MEETINGS_LIMIT = 50;

export function useGetMeetingsForReport(meetingId: string | null): {
    loadOptions: (search: string) => Promise<TwoLineOptionWithIcon[]>;
    selectedOption: TwoLineOptionWithIcon | null;
    isLoading: boolean;
} {
    const client = useApolloClient();

    const loadOptions = useCallback(
        async (search: string): Promise<TwoLineOptionWithIcon[]> => {
            const {data} = await client.query<
                GetMeetingsForReportQuery,
                GetMeetingsForReportVariables
            >({
                query: GET_MEETINGS_FOR_REPORT,
                variables: {
                    limit: MEETINGS_LIMIT,
                    sort: 'NUM_BOOKINGS_DESC' as MeetingSortOrder,
                    search: search || undefined,
                    hasBookingsInLast90Days: true,
                },
                fetchPolicy: 'network-only',
            });

            if (!data?.meetings) {
                return [];
            }

            return data.meetings.map((meeting) => getMeetingOption(meeting));
        },
        [client]
    );

    const {data, loading} = useQuery<
        GetMeetingForReportValueQuery,
        GetMeetingForReportValueVariables
    >(GET_MEETING_FOR_REPORT_VALUE, {
        variables: {id: meetingId!},
        skip: !meetingId,
        fetchPolicy: 'cache-first',
    });

    const selectedOption = meetingId && data?.meeting ? getMeetingOption(data.meeting) : null;

    return {
        loadOptions,
        selectedOption,
        isLoading: loading,
    };
}

export type {MeetingForReport};
